// Cookie consent helper. Single source of truth for tracking opt-in.
// Reads the same localStorage key written by CookieBanner / privacy settings,
// and emits a "peoples:consent-changed" event so the funnel tracker can react live.

const KEY = "peoples_cookie_consent_v1";
const EVENT = "peoples:consent-changed";

export type Consent = { analytics: boolean; marketing: boolean; at: number };

export function getConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Consent>;
    return {
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      at: Number(parsed.at) || Date.now(),
    };
  } catch {
    return null;
  }
}

export function setConsent(c: Pick<Consent, "analytics" | "marketing">): void {
  if (typeof window === "undefined") return;
  const full: Consent = { ...c, at: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(full));
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: full })); } catch { /* noop */ }
}

export function clearConsent(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY);
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: null })); } catch { /* noop */ }
}

export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

export function onConsentChange(cb: (c: Consent | null) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => cb((e as CustomEvent<Consent | null>).detail ?? getConsent());
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
